import React from 'react';
import { array, object, string } from 'prop-types';
import { types } from '../../../../../util/sdkLoader';

// import { staticExchangeRates } from '../../config/configStripe';
import {
  FormattedMessage,
  intlShape,
  injectIntl,
} from '../../../../../util/reactIntl';
import { displayPrice } from '../../../../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../../../../util/uiHelpers';
import { convertPrice, formatMoney } from '../../../../../util/currency';
import { createSlug } from '../../../../../util/urlHelpers';
import { isBookingProcessAlias } from '../../../../../transactions/transaction';

import {
  AspectRatioWrapper,
  Button,
  IconCollection,
  ResponsiveImage,
} from '../../../../../components';
import { useConfiguration } from '../../../../../context/configurationContext';
import { createResourceLocatorString } from '../../../../../util/routes';
import css from './ListCard.module.css';
import { isDeliveryOrShippingAvailable, isPickupRequired, sortPackages } from '../../../../../util/data';
import IconCard from '../../../../../components/SavedCardDetails/IconCard/IconCard';
import DistanceMapModal from './DistanceMapModal';

const { UUID } = types;

const isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;

// Function to format distance in km and miles
const formatDistance = (distance) => {
  if (!distance || !distance.kilometers || !distance.miles) return null;

  const distanceInKm = distance.kilometers.toFixed(1);
  const distanceInMiles = distance.miles.toFixed(1);

  return {
    km: distanceInKm,
    miles: distanceInMiles
  };
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};


const PriceMaybe = props => {
  const {
    price,
    listingType,
    defaultPriceKey,
    transactionProcessAlias,
    config,
    intl,
    selectedCurrency,
    additionalPrices,
    maxWidth
  } = props;

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(
    price,
    selectedCurrency,
    intl
  );

  return (
    <div>
      <div
        className={css.pricesContainer}
        style={{ maxWidth }}
      >
        <div className={css.price}>
          <div className={css.priceValue} title={priceTitle}>
            {formattedPrice}
          </div>
          {isBookable ? (
            <div className={css.perUnit}>
              <FormattedMessage
                id="ListingCard.perUnit"
                values={{ defaultPriceKey: defaultPriceKey || 'perDayPrice' }}
              />
              {/* <span className={css.seeMore}><FormattedMessage id='ListingCard.seeMore' /></span> */}
            </div>
          ) : null}
        </div>
        {additionalPrices && additionalPrices.length > 0 && additionalPrices.map((p, index) => (
          <div key={index} className={css.additionalPrice}>
            <span className={css.dividerLine}>|</span>
            {priceData(p.price, selectedCurrency, intl).formattedPrice}
            <span className={css.perText}>
              <FormattedMessage
                id="ListingCard.perUnit"
                values={{ defaultPriceKey: p.key }}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

function truncateText(text, maxLength) {
  if (text.length <= maxLength) {
    return text; // Return the original text if it's within or equal to the maxLength
  } else {
    return text.slice(0, maxLength) + '..'; // Truncate the text and add ellipsis
  }
}

export const ListCardComponent = props => {
  const config = useConfiguration();
  const [hoveredCall, setHoveredCall] = React.useState(false);
  const [callClicked, setCallClicked] = React.useState(false);
  const [cardWidth, setCardWidth] = React.useState(0);
  const [isMapModalOpen, setIsMapModalOpen] = React.useState(false);
  const cardRef = React.useRef(null);



  const {
    rental = {},
    intl,
    routes,
    history,
    currentUserCurrency,
    exchangeRates,
    onClick,
    queryID,
    additional,
    onActivateListing,
    currentUserCountryCode
  } = props;

  const id = rental.objectID;
  const {
    title = '',
    price,
    user: { displayName, publicData: uPublicData },
    publicData,
    distance,
    _geoloc,
  } = rental;

  const { storeName, rating } = uPublicData || {};

  // Get distance information if available
  const distanceInfo = distance ? formatDistance(distance) : null;

  const {
    category,
    city,
    listingType,
    defaultPriceKey,
    transactionProcessAlias,
    listingImages = [],
    prices,
    access_to_rentals,
    additionalFees,
    location,
  } = publicData || {};
  const address = location?.address;
  const pickupRequired = isPickupRequired(access_to_rentals);
  const deliveryOrShipping = isDeliveryOrShippingAvailable(access_to_rentals, additionalFees);

  const { aspectWidth = 1, aspectHeight = 1 } = config.layout.listingImage;

  const slug = createSlug(title);

  const convertedPrice = !!price
    ? convertPrice(
      exchangeRates,
      price.amount,
      price.currency,
      currentUserCurrency
    )
    : null;

  // Convert additional prices, order by short to long
  const additionalConvertedPrices = prices && Object.keys(prices || {}).length > 1
    ? Object.entries(sortPackages(prices, defaultPriceKey)).filter(([key]) => key !== defaultPriceKey).map(([key, value]) => {
      return {
        price: convertPrice(
          exchangeRates,
          value.amount,
          value.currency,
          currentUserCurrency
        ),
        key
      }
    })
    : [];

  const queryString =
    !!category && !!city && !!queryID
      ? `?city=${city.toLowerCase()}&category=${category.toLowerCase()}&queryID=${queryID}`
      : !!category && !!queryID
        ? `?category=${category.toLowerCase()}&queryID=${queryID}`
        : !!city && !!queryID
          ? `?city=${city.toLowerCase()}&queryID=${queryID}`
          : !!queryID
            ? `?queryID=${queryID}`
            : null;

  const firstImage = listingImages[0];
  const firstImageMaybe =
    !!firstImage && firstImage.secure_url ? (
      <ResponsiveImage
        alt={title}
        renderCloudinaryImage={{
          secure_url: firstImage.secure_url,
          alt: title,
          variantsToRender: [
            'listing-card',
            'listing-card-2x'
          ],
          defaultVariant: 'listing-card',
        }}
      />) : (
      <div className={css.noImageWrapper}>
        <svg
          color="#333"
          width="100px"
          height="100px"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M7.828 5l-1-1H22v15.172l-1-1v-.69l-3.116-3.117-.395.296-.714-.714.854-.64a.503.503 0 0 1 .657.046L21 16.067V5zM3 20v-.519l2.947-2.947a1.506 1.506 0 0 0 .677.163 1.403 1.403 0 0 0 .997-.415l2.916-2.916-.706-.707-2.916 2.916a.474.474 0 0 1-.678-.048.503.503 0 0 0-.704.007L3 18.067V5.828l-1-1V21h16.172l-1-1zM17 8.5A1.5 1.5 0 1 1 15.5 7 1.5 1.5 0 0 1 17 8.5zm-1 0a.5.5 0 1 0-.5.5.5.5 0 0 0 .5-.5zm5.646 13.854l.707-.707-20-20-.707.707z" />
          <path fill="none" d="M0 0h24v24H0z" />
        </svg>
      </div>
    );

  React.useEffect(() => {
    const updateWidth = () => {
      if (cardRef.current) {
        setCardWidth(cardRef?.current?.offsetWidth - 35);
      }
    };

    // Initial width measurement
    updateWidth();

    // Add resize listener
    window.addEventListener('resize', updateWidth);

    // Cleanup
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleView = (e) => {
    e.stopPropagation();
    onClick();
    history.push(
      createResourceLocatorString(
        'ListingPage',
        routes,
        { id, slug },
        { search: queryString }
      )
    );
  };

  const handleCall = (e) => {
    e.stopPropagation();
    setCallClicked(true);
    if (typeof window !== 'undefined') {
      window.location.href = `tel:${process.env.REACT_APP_TEAM_PHONE_NUMBER}`
    }
  };

  const handleDistanceClick = (e) => {
    e.stopPropagation();
    if (onActivateListing) {
      onActivateListing(new UUID(id));
    }
    if (!isDesktop) {
      setIsMapModalOpen(true);
    }
  };

  const distanceClickHandlerMaybe = onActivateListing
    ? {
      onClick: handleDistanceClick,
    }
    : {};

  const setActivePropsMaybe = onActivateListing
    ? {
      onMouseEnter: () => onActivateListing(new UUID(id)),
      onMouseLeave: () => onActivateListing(null),
    }
    : null;

  return (
    <div className={css.listCard} onClick={handleView} ref={cardRef}>
      {/* {additional ? <div className={css.additional}>Additional</div> : null} */}
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        {firstImageMaybe}
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.infoWrapper}>
          <PriceMaybe
            price={convertedPrice}
            listingType={listingType}
            transactionProcessAlias={transactionProcessAlias}
            defaultPriceKey={defaultPriceKey}
            config={config}
            intl={intl}
            selectedCurrency={currentUserCurrency}
            additionalPrices={additionalConvertedPrices}
            maxWidth={cardWidth}
          />
          <div className={css.mainInfo}>
            <div className={css.title}>{truncateText(title, 43)}</div>
            <div className={css.authorInfo}>
              <div className={css.authorName}>
                <FormattedMessage
                  id="ListCard.author"
                  values={{ authorName: <span className={css.authorText}>{truncateText(storeName || displayName, 33)}</span> }}
                />
                <IconCard className={css.verifyShield} brand="verifiedShield" />
              </div>
            </div>
            <div className={css.actionButtons}>
              <Button
                type="button"
                onMouseEnter={() => setHoveredCall(true)}
                onMouseLeave={() => setHoveredCall(false)}
                onClick={handleCall}
                className={css.button}
              >
                {(hoveredCall || callClicked)
                  ? <FormattedMessage id="ListCard.callButtonHoverText" values={{ teamNumber: process.env.REACT_APP_TEAM_PHONE_NUMBER }} />
                  : <FormattedMessage id="ListCard.callButtonText" />}
              </Button>
              <Button
                type="button"
                onClick={handleView}
                className={css.button}
              >
                <FormattedMessage id="ListCard.viewListingButtonText" />
              </Button>
            </div>
          </div>
        </div>
        <div className={css.locationContainer}>
          <div className={css.locationInfo}>
            {distanceInfo && (<div className={css.locationDistance} {...distanceClickHandlerMaybe}>
              <IconCollection name="red_location" />
              <span className={css.locationAway}>
                <FormattedMessage
                  id="ListCard.distance"
                  values={{
                    distance: currentUserCountryCode === 'US' ? distanceInfo.miles : distanceInfo.km,
                    unit: currentUserCountryCode === 'US' ? 'miles' : 'km',
                  }}
                /></span>
              <span className={css.locationText}>
                -
              </span>
            </div>)}
            <span className={css.locationText}>
              {!!city
                ? <FormattedMessage id='ListCard.location' values={{ pickupRequired, city }} />
                : <FormattedMessage id='ListCard.noLocation' values={{ pickupRequired }} />}
            </span>
          </div>
          {deliveryOrShipping ? (
            <div className={css.deliveryInfo}>
              <span className={css.truckIcon}> <IconCard brand="truck" /></span>
              <FormattedMessage id="ListCard.delivery" />
            </div>
          ) : null}
        </div>
      </div>
      {!isDesktop && (<DistanceMapModal
        isOpen={isMapModalOpen}
        onClose={(e) => {
          e.stopPropagation();
          setIsMapModalOpen(false);
        }}
        geolocation={_geoloc}
        address={address}
        title={title}
      />)}
    </div>
  );
};

ListCardComponent.propTypes = {
  intl: intlShape.isRequired,
  rental: object.isRequired,
  currentUserCurrency: string.isRequired,
  exchangeRates: array.isRequired,
};

export default injectIntl(ListCardComponent);
